// WormSticker.js — Stickers WORMS animés (thème du jeu phare) pour les chats VertinGo.
// Un sticker = un ver 🪱 avec une expression (emoji) et une animation CSS selon l'humeur.
// Envoyé dans un message de chat comme { sticker: '<id>' } → rendu par <WormSticker id=... />.
import React from 'react';
import styled, { keyframes } from 'styled-components';

// Catalogue : id (stocké dans le message), humeur (emoji visage), libellé, animation.
export const WORM_STICKERS = [
  { id: 'worm_joy', face: '😄', label: 'Joie', anim: 'bounce' },
  { id: 'worm_laugh', face: '😂', label: 'Rire', anim: 'roll' },
  { id: 'worm_taunt', face: '😜', label: 'Nargue', anim: 'wiggle' },
  { id: 'worm_angry', face: '😠', label: 'Colère', anim: 'shake' },
  { id: 'worm_sad', face: '😢', label: 'Détresse', anim: 'droop' },
  { id: 'worm_love', face: '😍', label: 'Amour', anim: 'pulse' },
  { id: 'worm_cool', face: '😎', label: 'Classe', anim: 'sway' },
  { id: 'worm_shock', face: '😱', label: 'Choc', anim: 'shake' },
  { id: 'worm_win', face: '🏆', label: 'Victoire', anim: 'jump' },
  { id: 'worm_boom', face: '🧨', label: 'Dynamite', anim: 'explode' },
];
const BY_ID = Object.fromEntries(WORM_STICKERS.map((s) => [s.id, s]));
export const isSticker = (id) => !!BY_ID[id];

const bounce = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}`;
const roll = keyframes`0%{transform:rotate(-14deg)}50%{transform:rotate(14deg)}100%{transform:rotate(-14deg)}`;
const wiggle = keyframes`0%,100%{transform:rotate(-8deg)}50%{transform:rotate(8deg) translateX(3px)}`;
const shake = keyframes`0%,100%{transform:translateX(0)}20%{transform:translateX(-4px)}40%{transform:translateX(4px)}60%{transform:translateX(-3px)}80%{transform:translateX(3px)}`;
const droop = keyframes`0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(4px) rotate(6deg)}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.15)}`;
const sway = keyframes`0%,100%{transform:rotate(-5deg)}50%{transform:rotate(5deg)}`;
const jump = keyframes`0%,100%{transform:translateY(0)}30%{transform:translateY(-12px)}45%{transform:translateY(0)}60%{transform:translateY(-6px)}`;
const explode = keyframes`0%,100%{transform:scale(1) rotate(0)}45%{transform:scale(1.05)}50%{transform:scale(1.25) rotate(-6deg)}55%{transform:scale(1.05)}`;
const ANIMS = { bounce, roll, wiggle, shake, droop, pulse, sway, jump, explode };

const Box = styled.span`
  position: relative; display: inline-flex; align-items: center; justify-content: center;
  width: ${({ $size }) => $size}px; height: ${({ $size }) => $size}px; user-select: none;
  .worm { font-size: ${({ $size }) => Math.round($size * 0.62)}px; line-height: 1;
    animation: ${({ $anim }) => ANIMS[$anim] || bounce} 1.1s ease-in-out infinite; }
  .face { position: absolute; right: -2px; top: -2px; font-size: ${({ $size }) => Math.round($size * 0.42)}px;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,.4)); }
`;

// Sticker animé (dans un message). `size` par défaut 72px.
export default function WormSticker({ id, size = 72 }) {
  const s = BY_ID[id];
  if (!s) return null;
  return (
    <Box $size={size} $anim={s.anim} title={s.label} role="img" aria-label={`Ver ${s.label}`}>
      <span className="worm">🪱</span>
      <span className="face">{s.face}</span>
    </Box>
  );
}
